import React from 'react';
import { Box, Tag, Text, TextButton } from '@wix/design-system';
import { categoryClick } from '@wix/bi-logger-app-market-data/v2';
import { AppPropertiesDataHooks } from './data-hooks';
import {
  useTranslation,
  useEssentials,
  useRouterContext,
  useSiteContext,
  Route,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import type { RoutePayloadMap } from '@wix/app-market-core-types';

export interface AppCollectionsProps {
  appId: string;
  collections: {
    slug: string;
    name: string;
  }[];
  subCategories?: boolean;
}

export const AppCollections = ({
  appId,
  collections,
  subCategories,
}: AppCollectionsProps) => {
  const { t } = useTranslation();
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();
  const { baseURL } = useSiteContext();

  function getButtonHref(path: RoutePath, payload: RoutePayloadMap[RoutePath]) {
    const hasHref = router.hasHref();
    if (!hasHref) {
      return undefined;
    }
    const route = new Route({
      path,
      payload,
      baseURL,
    });
    return route.toHref();
  }
  function onCollectionClick(collection: { slug: string; name: string }) {
    biLogger.report(
      categoryClick({
        app_id: appId,
        tag_name: collection.name,
        tag_type: 'collection',
        origin: 'more_apps_like_this',
      }),
    );
    router.navigateTo({
      path: RoutePath.COLLECTION,
      payload: {
        slug: collection.slug,
        referral: 'app_tag',
      },
    });
  }

  const sharedProps = ({
    collection,
    index,
  }: {
    collection: { slug: string; name: string };
    index: number;
  }): {
    href: string | undefined;
    dataHook: string;
    key: string;
    id: string;
    removable: boolean;
  } => {
    return {
      href: getButtonHref(RoutePath.COLLECTION, {
        slug: collection.slug,
        referral: 'app_tag',
      }),
      dataHook: `${AppPropertiesDataHooks.appCollection}-${index}`,
      key: collection.slug,
      id: collection.slug,
      removable: false,
    };
  };

  return (
    <Box
      dataHook={AppPropertiesDataHooks.featuredInContainer}
      direction="vertical"
    >
      <Text size="small" weight="bold">
        {t('solution.page.tagsBoxTitle')}
      </Text>
      <Box
        gap={subCategories ? '0px' : '6px'}
        flexWrap="wrap"
        marginTop={subCategories ? '2px' : '6px'}
      >
        {subCategories
          ? collections.map((collection, index) => {
              return (
                <Box key={index}>
                  <TextButton
                    as="a"
                    {...sharedProps({ collection, index })}
                    size="small"
                    weight="thin"
                    onClick={(e) => {
                      onCollectionClick(collection);
                      e.preventDefault();
                    }}
                  >
                    {collection.name}
                  </TextButton>
                  {index < collections.length - 1 && (
                    <span style={{ display: 'inline-block', color: '#116DFF' }}>
                      ,&nbsp;
                    </span>
                  )}
                </Box>
              );
            })
          : collections.map((collection, index) => {
              return (
                <Tag
                  {...sharedProps({ collection, index })}
                  onClick={(_id, e) => {
                    onCollectionClick(collection);
                    e.preventDefault();
                  }}
                  size="tiny"
                >
                  {collection.name}
                </Tag>
              );
            })}
      </Box>
    </Box>
  );
};
